/**
 * Optimal Building Suggestion Component
 * 
 * Displays the suggested optimal building for the hovered tile.
 * Ported from OptimalBuilding.mxml
 */

import {
    WOOD, CUTTER, SAWMILL, CUTTER_OLD,
    STONE, QUARRY, STONEMASON, QUARRY_OLD,
    IRON, MINE, FOUNDRY, MINE_OLD,
    FOOD, FARM, MILL, FARM_OLD, LAKE,
    GOLD, MANOR, BAZAAR, WHARF,
    CASA, CELLARIUM, CACHE, WALL, DEEP_WATER, WATER, TOWN_HALL,
    BARRACKS, CASTLE, CITY_GUARD_HOUSE, SHIPYARD,
    MOONGLOW, STABLES, TEMPLE, TRAINING_GROUND, WORKSHOP
} from '../core/Constants.js';
import { getTileImageKey } from '../utils/TileImageMap.js';
import { getMilbuildingSprite, MILBUILDINGS_SPRITE_MAP, getResbuildingSprite, RESBUILDINGS_SPRITE_MAP } from '../utils/SpriteSheet.js';
import { getImagePath, getImageBasePath } from '../utils/imagePath.js';

export class OptimalBuilding {
    constructor(containerElement, gameEngine, gridCanvas) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.gridCanvas = gridCanvas;
        this.currentTile = null;
        this.optimalTile = null;
        
        // Render initially
        this.render();
    }
    
    /**
     * Update for a specific tile
     */
    update(i, j) {
        this.currentTile = { i, j };
        
        if (!this.gameEngine || i < 0 || i >= 21 || j < 0 || j >= 21) {
            this.optimalTile = null;
            this.render();
            return;
        }
        
        // Calculate optimal building for this position
        this.calculateOptimalBuilding(i, j);
        this.render();
    }
    
    /**
     * Calculate optimal building for position
     */
    calculateOptimalBuilding(i, j) {
        // Use GameEngine's built-in method if available
        if (this.gameEngine.getOptimalTile) {
            const ANY = 0x8000;
            const result = this.gameEngine.getOptimalTile(ANY, i, j);
            if (result && result.tile !== undefined) {
                this.optimalTile = result.tile;
                this.optimalValue = result.value || 0;
            } else {
                this.optimalTile = null;
                this.optimalValue = 0;
            }
            return;
        }
        
        // Fallback: manual calculation
        // Get all possible tiles for this terrain
        const baseMap = this.gameEngine.water ? this.gameEngine.waterMap : this.gameEngine.landMap;
        const terrain = baseMap[i][j] & 0x0f00;
        
        // Get tileset for this terrain
        let tileset = [];
        const ANY = 0x8000;
        if (this.gameEngine.tilesets[ANY]) {
            tileset = [...this.gameEngine.tilesets[ANY]];
        }
        if (this.gameEngine.tilesets[terrain]) {
            tileset = [...tileset, ...this.gameEngine.tilesets[terrain]];
        }
        
        // Evaluate each possible tile
        let bestTile = null;
        let bestValue = -Infinity;
        
        const originalTile = this.gameEngine.map[i][j];
        
        for (const tile of tileset) {
            // Check terrain compatibility - compare with base map terrain
            const TERRAIN_TYPE = 0x0300;
            if ((baseMap[i][j] & TERRAIN_TYPE) !== (tile & TERRAIN_TYPE)) {
                continue;
            }
            
            // Temporarily place tile
            this.gameEngine.map[i][j] = tile;
            
            // Evaluate
            const value = this.gameEngine.eval3x3(i, j);
            
            if (value > bestValue) {
                bestValue = value;
                bestTile = tile;
            }
            
            // Restore
            this.gameEngine.map[i][j] = originalTile;
        }
        
        // Calculate delta (value gain from placing this tile)
        const originalValue = this.gameEngine.eval3x3(i, j);
        this.optimalTile = bestTile;
        this.optimalValue = bestValue - originalValue;
    }
    
    /**
     * Get building name for tile
     */
    getBuildingName(tile) {
        // Map tile constants to names
        const names = {
            [TOWN_HALL]: 'Basilica',
            [WALL]: 'City Wall',
            [DEEP_WATER]: 'Deep Water',
            [WATER]: 'Water Spot',
            [WOOD]: 'Wood',
            [CUTTER]: 'Forester\'s Hut',
            [SAWMILL]: 'Sawmill',
            [CUTTER_OLD]: 'Forester\'s Hut (Old)',
            [STONE]: 'Stone',
            [QUARRY]: 'Stone Mine',
            [STONEMASON]: 'Mason\'s Hut',
            [QUARRY_OLD]: 'Stone Mine (Old)',
            [IRON]: 'Iron',
            [MINE]: 'Iron Mine',
            [FOUNDRY]: 'Smelter',
            [MINE_OLD]: 'Iron Mine (Old)',
            [FOOD]: 'Food',
            [FARM]: 'Farm Estate',
            [MILL]: 'Grain Mill',
            [FARM_OLD]: 'Farm Estate (Old)',
            [LAKE]: 'Lake',
            [GOLD]: 'Gold',
            [MANOR]: 'Manor',
            [BAZAAR]: 'Bazaar',
            [WHARF]: 'Wharf',
            [CASA]: 'Casa',
            [CELLARIUM]: 'Cellarium',
            [CACHE]: 'Cache',
            [BARRACKS]: 'Barracks',
            [CASTLE]: 'Castle',
            [CITY_GUARD_HOUSE]: 'Guardhouse',
            [SHIPYARD]: 'Shipyard',
            [MOONGLOW]: 'Sorcerer\'s Tower',
            [STABLES]: 'Stables',
            [TEMPLE]: 'Temple',
            [TRAINING_GROUND]: 'Training Arena',
            [WORKSHOP]: 'Blacksmith',
        };
        
        return names[tile] || `Tile 0x${tile.toString(16)}`;
    }
    
    /**
     * Render the component
     */
    render() {
        if (!this.optimalTile || !this.currentTile) {
            this.container.innerHTML = `
                <div class="optimal-building">
                    <h4>Optimal Building</h4>
                    <p class="no-suggestion">Hover over a tile to see suggestion</p>
                </div>
            `;
            return;
        }
        
        // Get building icon
        const imageKey = getTileImageKey(this.optimalTile);
        const buildingKey = this.getBuildingKey(this.optimalTile);
        const buildingName = this.getBuildingName(this.optimalTile);
        
        // If no building key (e.g., water tiles), don't show an image
        if (!buildingKey) {
            this.container.innerHTML = `
                <div class="optimal-building">
                    <h4>Optimal Building</h4>
                    <div class="optimal-tile-info">
                        <div class="tile-name">
                            <span>${buildingName}</span>
                        </div>
                        <div class="tile-value">Value: ${this.formatNumber(this.optimalValue)}</div>
                        <div class="tile-position">Position: (${this.currentTile.i}, ${this.currentTile.j})</div>
                    </div>
                </div>
            `;
            return;
        }
        
        // Check if this is a military or resource building in the sprite sheet
        const isMilbuilding = buildingKey && MILBUILDINGS_SPRITE_MAP[buildingKey];
        const isResbuilding = buildingKey && RESBUILDINGS_SPRITE_MAP[buildingKey];
        
        // Get image source (async for sprite sheet, sync for regular images)
        let imageSrc = '';
        if (isMilbuilding) {
            // Use military buildings sprite sheet - will be loaded async
            getMilbuildingSprite(buildingKey).then(dataURL => {
                if (dataURL) {
                    const img = this.container.querySelector('.building-stats-icon');
                    if (img) img.src = dataURL;
                }
            }).catch(err => {
                console.warn('Failed to load sprite from milbuildings.png:', err);
            });
            // Set placeholder initially
            imageSrc = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
        } else if (isResbuilding) {
            // Use resource buildings sprite sheet - will be loaded async
            getResbuildingSprite(buildingKey).then(dataURL => {
                if (dataURL) {
                    const img = this.container.querySelector('.building-stats-icon');
                    if (img) img.src = dataURL;
                }
            }).catch(err => {
                console.warn('Failed to load sprite from resbuildings.png:', err);
            });
            // Set placeholder initially
            imageSrc = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
        } else {
            const filename = this.getImageFilename(imageKey || buildingKey);
            // Determine if it should use UI folder (resource icons, UI icons like icon_build_slots, or wall)
            const isUI = buildingKey && (
                buildingKey.startsWith('resource_') || 
                buildingKey.startsWith('icon_resource_') || 
                buildingKey.startsWith('icon_') ||
                buildingKey === 'building_wall'
            );
            const imageBase = getImageBasePath();
            imageSrc = isUI 
                ? `${imageBase}/ui/${filename}.png`
                : `${imageBase}/buildings/${filename}.png`;
        }
        
        this.container.innerHTML = `
            <div class="optimal-building">
                <h4>Optimal Building</h4>
                <div class="optimal-tile-info">
                    <div class="tile-name" style="display: flex; align-items: center; gap: 8px;">
                        <img src="${imageSrc}" 
                             alt="${buildingName}" 
                             title="${buildingName}"
                             class="building-stats-icon"
                             style="width: 32px; height: 32px; object-fit: contain; flex-shrink: 0;"
                             onerror="this.style.display='none'" />
                        <span>${buildingName}</span>
                    </div>
                    <div class="tile-value">Value: ${this.formatNumber(this.optimalValue)}</div>
                    <div class="tile-position">Position: (${this.currentTile.i}, ${this.currentTile.j})</div>
                    <button class="btn-place-suggested" id="btn-place-optimal" title="Place Suggested (Enter)">
                        <img src="${getImagePath('ui/star_icon.png')}" alt="Place Suggested" class="star-icon" onerror="this.style.display='none'">
                    </button>
                </div>
            </div>
        `;
        
        // Setup event listener
        const btn = document.getElementById('btn-place-optimal');
        if (btn) {
            btn.addEventListener('click', () => {
                this.placeSuggested();
            });
        }
    }
    
    /**
     * Place the suggested building
     */
    placeSuggested() {
        if (!this.optimalTile || !this.currentTile || !this.gameEngine) {
            console.warn('Cannot place suggested building: missing optimalTile, currentTile, or gameEngine');
            return;
        }
        
        // Validate tile position
        if (this.currentTile.i < 0 || this.currentTile.i >= 21 || 
            this.currentTile.j < 0 || this.currentTile.j >= 21) {
            console.warn(`Invalid tile position: (${this.currentTile.i}, ${this.currentTile.j})`);
            return;
        }
        
        // Find the key for this tile
        const keyChar = this.gameEngine.tileToChar(this.optimalTile);
        
        // Check if we got a valid key (not a space character which indicates tile not found)
        if (keyChar && keyChar !== ' ') {
            this.gameEngine.placeByChar(keyChar, this.currentTile.i, this.currentTile.j);
            
            // Trigger UI update
            if (this.gridCanvas) {
                this.gridCanvas.update();
            }
        } else {
            console.warn(`Cannot find key binding for tile: 0x${this.optimalTile.toString(16)}`);
        }
    }
    
    /**
     * Format number
     */
    formatNumber(num) {
        return Math.round(num).toLocaleString();
    }
    
    /**
     * Get building key for image lookup
     */
    getBuildingKey(tile) {
        const keyMap = {
            [TOWN_HALL]: 'building_townhall',
            [CUTTER]: 'building_forester',
            [CUTTER_OLD]: 'building_forester',
            [SAWMILL]: 'building_sawmill',
            [QUARRY]: 'building_quarry',
            [QUARRY_OLD]: 'building_quarry',
            [STONEMASON]: 'building_stonemason',
            [MINE]: 'building_mine',
            [MINE_OLD]: 'building_mine',
            [FOUNDRY]: 'building_foundry',
            [FARM]: 'building_farm',
            [FARM_OLD]: 'building_farm',
            [MILL]: 'building_mill',
            [MANOR]: 'building_manor',
            [BAZAAR]: 'building_bazaar',
            [WHARF]: 'building_wharf',
            [CASA]: 'building_casa',
            [CELLARIUM]: 'building_cellarium',
            [CACHE]: 'building_cache',
            [BARRACKS]: 'building_barracks',
            [CASTLE]: 'building_castle',
            [CITY_GUARD_HOUSE]: 'building_guard_house',
            [SHIPYARD]: 'building_shipyard',
            [MOONGLOW]: 'building_moonglow',
            [STABLES]: 'building_stable',
            [TEMPLE]: 'building_temple',
            [TRAINING_GROUND]: 'building_training_ground',
            [WORKSHOP]: 'building_workshop',
            [WALL]: 'building_wall',
            [DEEP_WATER]: null, // Water tiles use background, no image
            [WATER]: null, // Water tiles use background, no image
            [WOOD]: 'resource_wood',
            [STONE]: 'resource_stone',
            [IRON]: 'resource_iron',
            [LAKE]: 'resource_lake',
            [FOOD]: 'resource_lake'
        };
        return keyMap[tile] || null;
    }
    
    /**
     * Get image filename from asset key
     */
    getImageFilename(key) {
        if (!key) return 'building_placeholder';
        
        // Map asset keys to actual filenames (matching AssetLoader paths)
        const filenameMap = {
            'building_townhall': 'basilica-fix-64',
            'building_forester': 'forester-v6-64',
            'building_sawmill': 'sawmill-64',
            'building_quarry': 'quarry-2-64',
            'building_stonemason': 'stonemasonery-64',
            'building_mine': 'iron-mine-64',
            'building_foundry': 'smelter-64',
            'building_farm': 'farm-v3-64',
            'building_mill': 'grain-mill-64-fix',
            'building_manor': 'villa-64',
            'building_bazaar': 'market-64',
            'building_wharf': 'ports-front-flip-64',
            'building_casa': 'basic_house-64',
            'building_cellarium': 'warehouse-v2-64',
            'building_cache': 'hideaway-64',
            'building_barracks': 'barracks-v2-64',
            'building_castle': 'castle-64-fix',
            'building_guard_house': 'guard\'s_house-v2-64',
            'building_shipyard': 'shipyard-front-flip-64',
            'building_moonglow': 'sorcerer\'s-tower-v2-64',
            'building_stable': 'stable-v2-fix-64',
            'building_temple': 'academy',
            'building_training_ground': 'training_arena-64',
            'building_workshop': 'blacksmith-v2-64',
            'building_wall': 'icon_build_slots', // Wall uses build slots icon from UI folder
            'resource_wood': 'icon_resource_wood',
            'resource_stone': 'icon_resource_stone',
            'resource_iron': 'icon_resource_iron',
            'resource_lake': 'icon_resource_grain'
        };
        
        // Check if it's a resource icon (should use UI folder)
        if (key.startsWith('resource_') || key.startsWith('icon_resource_')) {
            const resourceKey = key.replace('resource_', 'icon_resource_');
            return resourceKey;
        }
        
        return filenameMap[key] || key.replace('building_', '').replace(/_/g, '-') + '-64';
    }
}

